import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatPhoneNumber, stripPhoneHyphens } from '../utils/phoneFormat';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Paper,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

import { ErrorAlert, SuccessAlert } from '../components/Alerts';
import apiService from '../services/api';
import { User, AddressFields } from '../types';
import { useAuth } from '../auth/AuthContext';
import { scrollToFirstError } from '../utils/formValidation';
import AddressInput from '../components/AddressInput';

// 入力バリデーション用の型
interface ValidationErrors {
  last_name?: string;
  first_name?: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  address?: string;
}

const UserProfile: React.FC = () => {
  const { userId } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<User>({
    last_name: '',
    first_name: '',
    email: '',
    phone: '',
    birth_date: new Date(),
    address: ''
  });
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    // APIからユーザープロフィールを取得
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiService.getUser(userId);
        
        if (response.success && response.data) {
          setProfileData({
            ...response.data,
          });
          // バックエンドが last_name / first_name を直接返す
          setLastName(response.data.last_name ?? '');
          setFirstName(response.data.first_name ?? '');
        } else {
          setError('プロフィール情報の取得に失敗しました');
        }
      } catch (err) {
        setError('プロフィール情報の取得中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  // フォーム入力の変更を処理
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const processed = name === 'phone' ? formatPhoneNumber(value) : value;
    setProfileData((prev) => ({
      ...prev,
      [name]: processed
    }));
    
    // 入力時にバリデーションエラーをクリア
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined
      }));
    }
  };


  // 入力内容のバリデーション
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    // 名前のバリデーション
    if (!lastName.trim()) {
      errors.last_name = '姓は必須です';
    }
    if (!firstName.trim()) {
      errors.first_name = '名は必須です';
    }
    
    // メールアドレスのバリデーション
    if (!profileData.email || profileData.email.trim() === '') {
      errors.email = 'メールアドレスは必須です';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileData.email)) {
        errors.email = '有効なメールアドレスを入力してください';
      }
    }
    
    // 電話番号のバリデーション
    if (!profileData.phone || profileData.phone.trim() === '') {
      errors.phone = '電話番号は必須です';
    } else {
      const digitsOnly = stripPhoneHyphens(profileData.phone);
      if (digitsOnly.length < 10 || digitsOnly.length > 11) {
        errors.phone = '有効な電話番号を入力してください';
      }
    }
    
    // 生年月日のバリデーション
    if (!profileData.birth_date) {
      errors.birth_date = '生年月日は必須です';
    }
    
    // エラーがあればセットして失敗を返す
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const errorFieldIds = Object.keys(errors) as (keyof ValidationErrors)[];
      scrollToFirstError(errorFieldIds);
      return false;
    }
    
    return true;
  };

  // プロフィール更新処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーションチェック
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // APIに送信するデータを整形（姓・名を別フィールドで送信）
      const submitData = {
        ...profileData,
        last_name: lastName.trim(),
        first_name: firstName.trim(),
      };
      
      const response = await apiService.updateUser(userId, submitData);
      
      if (response.success) {
        setSuccess('プロフィール情報を更新しました');
        // 最新のデータを取得するため、再度useEffectが実行される
        // window.location.reload(); の代わりにこのパターンを使用可能
      } else {
        setError(response.error || 'プロフィール情報の更新に失敗しました');
      }
    } catch (err) {
      setError('プロフィール情報の更新中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pt: 0, px: 0, pb: 2, m: 0 }}>
      {error && <ErrorAlert message={error} onClose={() => setError(null)} />}
      {success && <SuccessAlert message={success} onClose={() => setSuccess(null)} />}
      
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to='/mypage'
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            color: '#9e9d9d',
            mb: 1,
          }}
        >
          マイページにもどる
        </Button>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography sx={{
          fontWeight: 'fontWeightBold',
          fontSize: '20px',
          textAlign: 'center',
          mb: 3,
        }}>プロフィール変更</Typography>

        {/* 店舗の事前登録との突合（名寄せ）はメール・電話・生年月日の3点で行うため、
            届け出内容と揃っていないと店舗側に「LINE未連携」の行が残る。 */}
        <Typography sx={{
          fontSize: '12px',
          color: '#666',
          lineHeight: 1.6,
          mb: 2,
        }}>
          勤務先店舗に届け出た内容と同じメールアドレス・電話番号・生年月日を登録してください。
          内容が異なると、店舗側の登録情報と結びつかないことがあります。
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={2} sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!validationErrors.last_name}>
                    <InputLabel htmlFor="last_name">姓（苗字）</InputLabel>
                    <OutlinedInput
                      id="last_name"
                      name="last_name"
                      label="姓（苗字）"
                      value={lastName}
                      onChange={(e) => {
                        const val = isComposing ? e.target.value : e.target.value.replace(/[\s　]/g, '');
                        setLastName(val);
                        if (validationErrors.last_name) {
                          setValidationErrors(prev => ({ ...prev, last_name: undefined }));
                        }
                      }}
                      onCompositionStart={() => setIsComposing(true)}
                      onCompositionEnd={(e) => {
                        setIsComposing(false);
                        const val = (e as React.CompositionEvent<HTMLInputElement>).currentTarget.value.replace(/[\s　]/g, '');
                        setLastName(val);
                      }}
                    />
                    {validationErrors.last_name && (
                      <FormHelperText>{validationErrors.last_name}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth error={!!validationErrors.first_name}>
                    <InputLabel htmlFor="first_name">名（名前）</InputLabel>
                    <OutlinedInput
                      id="first_name"
                      name="first_name"
                      label="名（名前）"
                      value={firstName}
                      onChange={(e) => {
                        const val = isComposing ? e.target.value : e.target.value.replace(/[\s　]/g, '');
                        setFirstName(val);
                        if (validationErrors.first_name) {
                          setValidationErrors(prev => ({ ...prev, first_name: undefined }));
                        }
                      }}
                      onCompositionStart={() => setIsComposing(true)}
                      onCompositionEnd={(e) => {
                        setIsComposing(false);
                        const val = (e as React.CompositionEvent<HTMLInputElement>).currentTarget.value.replace(/[\s　]/g, '');
                        setFirstName(val);
                      }}
                    />
                    {validationErrors.first_name && (
                      <FormHelperText>{validationErrors.first_name}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!validationErrors.email}>
                    <InputLabel htmlFor="email">メールアドレス</InputLabel>
                    <OutlinedInput
                      id="email"
                      name="email"
                      label="メールアドレス"
                      type="email"
                      value={profileData.email}
                      onChange={handleInputChange}
                    />
                    {validationErrors.email && (
                      <FormHelperText>{validationErrors.email}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!validationErrors.phone}>
                    <InputLabel htmlFor="phone">電話番号</InputLabel>
                    <OutlinedInput
                      id="phone"
                      name="phone"
                      label="電話番号"
                      type="tel"
                      placeholder="例: 090-1234-5678"
                      value={profileData.phone}
                      onChange={handleInputChange}
                    />
                    {validationErrors.phone && (
                      <FormHelperText>{validationErrors.phone}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" error={!!validationErrors.birth_date}>
                    <InputLabel htmlFor="birth-date-input">生年月日</InputLabel>
                    <OutlinedInput
                      id="birth-date-input"
                      name="birth_date"
                      type="text"
                      value={profileData.birth_date}
                      label="生年月日"
                      error={!!validationErrors.birth_date}
                      disabled={true}
                    />
                    {validationErrors.birth_date && (
                      <FormHelperText>{validationErrors.birth_date}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <AddressInput
                    value={{
                      postal_code: profileData.postal_code || '',
                      prefecture: profileData.prefecture || '',
                      city: profileData.city || '',
                      street: profileData.street || '',
                      building: profileData.building || '',
                    }}
                    onChange={(addr: AddressFields) => setProfileData(prev => ({ ...prev, ...addr }))}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    {saving ? <CircularProgress size={24} /> : '保存する'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default UserProfile;